/*
 * pogi-buy-box-pads — ISOLATED pads clone of pogi-buy-box (own element + JS file).
 * Identical 2-axis shell (PILLS = Quantity 20/40, TILE = Size M/L/XL footprint
 * glyphs). Only difference vs the generic: the pads role ladder. Per pads-
 * components.jsx: 20 -> "Starter", 40 -> "Best value" (clay). Kept separate so it
 * can never touch the shared wipes buy box.
 */

/* i18n: strings come from the data blob's `i18n` object (Liquid renders them via
   `| t` from locales/*.json pogi.bb.*). EVERY read falls back to the exact English
   literal that used to be hardcoded, so a missing key can never change the EN site.
   [[var]] placeholders (not {{ }}) so Liquid's t-filter never eats them. */
const pogiTpl = (I, k, fb, vars) => {
  let s = (I && I[k]) || fb;
  if (vars) for (const key of Object.keys(vars)) s = s.split('[[' + key + ']]').join(String(vars[key]));
  return s;
};

/* Market-currency guard for JS-DERIVED secondary prices. The variant prices in the
   data blob are the shop BASE-currency cents (USD) — not presentment-converted — so
   formatting them in a non-USD market shows a wrong number (e.g. "¥12"). The real
   prices localize via Liquid `| money`. To never show a wrong derived figure off-USD
   we HIDE the derived per-unit ("¢/unit") + savings lines when the active market
   currency isn't USD (the shop base). On USD this is true → identical behavior. */
const SHOW_DERIVED = !(window.Shopify && Shopify.currency && Shopify.currency.active) || Shopify.currency.active === 'USD';

/* Concrete arrival window for the ship nudge ("money moves" #2, 2026-06-12).
   Same math as the cart page (4pm cutoff, business days, 2-4 biz-day window). */
const pogiArrives = (I) => {
  const M = pogiTpl(I, 'js_months', 'Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec').split(',');
  const we = (d) => d.getDay() === 0 || d.getDay() === 6;
  const nb = (d) => { const x = new Date(d); do { x.setDate(x.getDate() + 1); } while (we(x)); return x; };
  const ab = (s, n) => { const x = new Date(s); let a = 0; while (a < n) { x.setDate(x.getDate() + 1); if (!we(x)) a++; } return x; };
  const now = new Date();
  const cut = new Date(now); cut.setHours(16, 0, 0, 0);
  const ship = (!we(now) && now < cut) ? now : nb(now);
  const a = ab(ship, 2), b = ab(ship, 4);
  const fmt = (d) => pogiTpl(I, 'js_date', '[[mon]] [[d]]', { mon: M[d.getMonth()], d: d.getDate() });
  const fmtShort = (d) => pogiTpl(I, 'js_date_short', '[[d]]', { d: d.getDate() });
  const range = fmt(a) + pogiTpl(I, 'js_range_join', '–') + (a.getMonth() === b.getMonth() ? fmtShort(b) : fmt(b));
  return pogiTpl(I, 'js_arrives', 'Arrives [[range]].', { range });
};

/* k = stable role key (i18n lookup + supplyDesc logic); t = English fallback label */
const ROLE = {
  20: { k: 'starter', t: 'Starter' },
  40: { k: 'value', t: 'Best value', accent: true },
};
// Fallback grouping by count when the pogi.pack_format metafield isn't set.
const FORMAT_BY_COUNT = { 100: 'tub', 400: 'tub', 120: 'packs', 240: 'packs' };
const ICONS = {
  sprout: '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20v-8"/><path d="M12 12c0-3.3 2.7-6 6-6 0 3.3-2.7 6-6 6z"/><path d="M12 14c0-2.8-2.2-5-5-5 0 2.8 2.2 5 5 5z"/></svg>',
  droplet: '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2.6S5 9.2 5 14a7 7 0 0 0 14 0c0-4.8-7-11.4-7-11.4z"/></svg>',
};
// Stable size key from exact dimension tokens (EN inches; localized values use metric).
// Rendered size words are display-only and never participate in matching.
const SIZE_KEY = { '18x24': 'medium', '45x60': 'medium', '24x24': 'large', '60x60': 'large', '24x35': 'xl', '60x90': 'xl' };
const SIZE_FIT_EN = { medium: 'Small breeds & puppies', large: 'Most dogs', xl: 'Large breeds & seniors' };
// NICE round cm per size (owner rule, NOT raw ceil of the inch dims). Mirrors the
// PAD_MAP wc/hc in pogi-sizing-modal.js. The dims t() carries BOTH unit pairs and
// each locale's js_dims picks one (EN → "[[w]] × [[h]] in", JA → "[[wc]] × [[hc]] cm"),
// so EN stays byte-identical (inches from the parsed option value) while /ja shows
// these nice cm instead of the raw-ceil cm in the translated option string.
const SIZE_CM = { medium: { wc: 45, hc: 60 }, large: { wc: 60, hc: 60 }, xl: { wc: 60, hc: 90 } };

/* POGI L10N FIX (2026-07-23): selling-plan names are LOCALIZED per storefront
   locale (9 locales: en/ja/fr/de/es/it + nl/pl/pt queued). The old parsers were
   EN(+JA)-only, so every EU plan name parsed null and the frequency picker fell
   back to the first N product plans = WRONG cadences offered off-EN. Unit-word
   alternations + a no-digit=every-1 branch (fr «tous les mois», de „jeden Monat",
   es «cada mes», pl „co miesiąc", pt «mensal»). Match order month→week→day MUST
   hold: PL week words (tydzień/tygodni) contain the day stems (dzie/dni).
   Trip-tested against all live plan names ×6 locales + nl/pl/pt synthetics. */
const POGI_FREQ_WORDS = {
  month: 'month|か月|ヶ月|mois|monat|maand|miesi|mensil|mensual|mensal|mese|mesi|meses|mês|mes\\b',
  week: 'week|weke|週間|semaine|woche|wochen|settiman|semanas|semana|tygod|tydzi|semanal|wekelijks',
  day: 'day|日ごと|jour|tage|tag\\b|giorn|día|dias|dag\\b|dagen|dzie|dni\\b|dia\\b',
};
const pogiParseFreq = (raw) => {
  const s = String(raw).toLowerCase().replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xFEE0));
  // Pass 1: explicit "N <unit>" wins; with several numeric clauses the LAST one is
  // the frequency ("1 month supply – delivery every 2 weeks" / "Vorrat für einen
  // Monat – Lieferung alle 2 Wochen" must both parse week:2, never month:1).
  // matchAll (not match, which only sees the FIRST per-unit hit) so SAME-unit
  // compounds also keep the last clause ("2 weeks starter – every 4 weeks" = week:4).
  let best = null;
  for (const u of ['month', 'week', 'day']) {
    for (const m of s.matchAll(new RegExp('(\\d+)\\s*(?:' + POGI_FREQ_WORDS[u] + ')', 'g'))) {
      if (!best || m.index > best.idx) best = { u: u, n: parseInt(m[1], 10), idx: m.index };
    }
  }
  if (best) return { u: best.u, n: best.n };
  // Pass 2: a bare unit word = every-1 (fr «tous les mois», de „jeden Monat", pl „co miesiąc").
  for (const u of ['month', 'week', 'day']) {
    if (new RegExp(POGI_FREQ_WORDS[u]).test(s)) return { u: u, n: 1 };
  }
  return null;
};

// A11Y (audit P3, 2026-07-23): the buy box's radio-group containers, for the
// roving-tabindex pattern. .pogi-bb__purchase and [data-pogi-freq-opts] hold
// role="radio" buttons without a radiogroup role in the Liquid, so they are
// matched explicitly. The sizing modal's radios are deliberately NOT covered —
// it owns its own DOM lifecycle. Selectors absent from a given clone are
// harmless (querySelectorAll/closest simply never match).
const POGI_ROVE_GROUPS = '[data-pogi-formats], [data-pogi-sizes], [data-pogi-tiles], [data-pogi-counts], .pogi-bb__purchase, [data-pogi-freq-opts]';
// Eligible = focusable-for-roving: not disabled, not [hidden], and rendered.
// offsetParent is the visibility check: it is null when the radio OR any
// ancestor is display:none (the clones hide invalid pills via style.display
// and whole panels via [hidden]) — one check covers both, and none of these
// radios are position:fixed (the one case offsetParent cannot see). Chosen
// over getComputedStyle for cost (Codex countersign #19, 2026-07-23).
const POGI_ROVE_ELIGIBLE = (b) => !b.disabled && !b.hidden && b.offsetParent !== null;

class PogiBuyBoxPads extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-pogi-data]');
    if (!dataEl) return;
    try { this.data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    this.I = this.data.i18n || {};
    this.t = (k, fb, vars) => pogiTpl(this.I, k, fb, vars);

    this.tilePos = parseInt(this.dataset.tilePos, 10) || 0;
    this.pillsPos = parseInt(this.dataset.pillsPos, 10) || 1;
    this.tileKind = this.dataset.tileKind || 'scent';
    this.estPerDay = parseFloat(this.dataset.estPerDay) || 4;
    this.estUnit = this.dataset.estUnit || 'weeks';
    this.unitNoun = this.dataset.unitNoun || 'unit';
    this.shipThreshold = parseInt(this.dataset.shipThreshold, 10) || 2500;
    this.moneyFormat = this.data.moneyFormat || '${{amount}}';
    this.currency = this.data.currency || null; // presentment ISO (USD/EUR/JPY…) for market-aware formatting
    // Zero-decimal currency (JPY/KRW…): the "¢/unit" anchor is meaningless — show
    // whole-currency per-unit instead. USD/EUR keep the existing ¢ path untouched.
    this.zeroDec = (() => { try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).resolvedOptions().maximumFractionDigits === 0; } catch (e) { return false; } })();
    this.section = this.closest('.shopify-section') || document;
    this.nativePicker = this.section.querySelector('variant-picker');
    this.nativeForm = document.getElementById(this.dataset.formId);

    const sel = this.data.variants.find((v) => v.id === this.data.selectedId) || this.data.variants[0];
    this.pill = sel ? sel.options[this.pillsPos - 1] : null;
    this.tile = this.tilePos ? sel.options[this.tilePos - 1] : null;
    this.intervalCount = parseInt(this.dataset.intervalCount, 10) || 3;
    this.subscribe = false; // one-time default (owner call 2026-06-11)
    this.intervalPlans = [];
    this.planId = null;
    this.#applyVariantPlans();
    this.qty = 1;

    this.pills = this.#uniq(this.data.variants.map((v) => v.options[this.pillsPos - 1]));
    this.tiles = this.tilePos ? this.#uniq(this.data.variants.map((v) => v.options[this.tilePos - 1])) : [];

    this.#renderPills();
    this.#renderTiles();
    this.#renderFrequencies();
    this.#ensureSellingPlanInput();
    this.#bind();
    this.section.addEventListener('variant:update', () => this.update());
    this.update();
    this.#injectGalleryBadge();
    this.#bindPressModal();
  }

  // §13.4 — the mobile press card opens a native <dialog>. Backdrop click + the
  // close button both dismiss it. No-op when the card/dialog aren't rendered.
  #bindPressModal() {
    const openBtn = this.querySelector('[data-pogi-press-open]');
    const modal = this.querySelector('[data-pogi-press-modal]');
    if (!openBtn || !modal || typeof modal.showModal !== 'function') return;
    openBtn.addEventListener('click', () => modal.showModal());
    modal.querySelector('[data-pogi-press-close]')?.addEventListener('click', () => modal.close());
    modal.addEventListener('click', (e) => { if (e.target === modal) modal.close(); });
  }

  // §26.1 — pin a BESTSELLER chip top-left over the hero gallery tile. The gallery
  // lives in the same product-information section as the buy box, so we reach it
  // from here. Idempotent (guards against the variant:update re-render).
  #injectGalleryBadge() {
    const text = (this.dataset.galleryBadge || '').trim();
    if (!text) return;
    const tile = this.section.querySelector('.product-media-container');
    if (!tile || tile.querySelector('.pogi-gallery-badge')) return;
    const chip = document.createElement('span');
    chip.className = 'pogi-gallery-badge';
    chip.textContent = text;
    tile.appendChild(chip);
  }

  #uniq(a) { return a.filter((x, i) => x != null && a.indexOf(x) === i); }
  #esc(s) { return String(s).replace(/"/g, '&quot;').replace(/</g, '&lt;'); }

  money(cents) {
    const amt = cents / 100;
    // Market-aware: format in the presentment currency (follows the country/
    // currency switch) instead of the shop's base money_format. Intl handles
    // zero-decimal currencies (JPY) + per-locale symbol placement.
    if (this.currency) {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).format(amt);
      } catch (e) { /* unknown ISO — fall back to the template below */ }
    }
    const grp = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return this.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, grp(amt.toFixed(2)))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, grp(Math.round(amt).toString()))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, grp(amt.toFixed(2)))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, grp(Math.round(amt).toString()));
  }
  moneyWhole(cents) { return this.money(Math.round(cents / 100) * 100).replace(/[.,]00\b/, ''); }
  // MAIN price display. The variant `price` in the blob is base-shop cents (USD), so
  // JS money() can only render the right NUMBER on USD. Off the base currency we instead
  // show the variant's Liquid `| money` string (true presentment ¥/€…). On USD, SHOW_DERIVED
  // is true → we keep the exact JS money() output, so the US string stays byte-identical.
  priceMoney(v) { return (!SHOW_DERIVED && v && v.money) ? v.money : this.money(v ? v.price : 0); }
  subPriceMoney(v) { return (!SHOW_DERIVED && v && v.subMoney) ? v.subMoney : null; }
  parseCount(v) {
    if (!v) return 0;
    const m = String(v).match(/(\d[\d,]*)\s*-?\s*count/i);
    if (m) return parseInt(m[1].replace(/,/g, ''), 10);
    const n = String(v).match(/\d[\d,]*/);
    return n ? parseInt(n[0].replace(/,/g, ''), 10) : 0;
  }
  variantObj(pillVal) {
    return this.data.variants.find((v) => v.options[this.pillsPos - 1] === pillVal && (this.tilePos === 0 || v.options[this.tilePos - 1] === this.tile));
  }
  // grouping: metafield pack_format wins, else count fallback, else single group
  formatOf(pillVal) {
    const v = this.data.variants.find((x) => x.options[this.pillsPos - 1] === pillVal);
    if (v && v.packFormat) return v.packFormat === 'packs' ? 'packs' : 'tub';
    return FORMAT_BY_COUNT[this.parseCount(pillVal)] || 'tub';
  }
  dims(v) { const s = String(v).replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0)); const m = s.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/); return m ? { w: parseFloat(m[1]), h: parseFloat(m[2]) } : null; }
  estN(count) { return this.estUnit === 'days' ? Math.max(1, Math.round(count / this.estPerDay)) : Math.max(1, Math.round(count / this.estPerDay / 7)); }
  unitWord(n) {
    const plural = n !== undefined && n !== 1;
    return this.estUnit === 'days'
      ? this.t(plural ? 'js_unit_days' : 'js_unit_day', plural ? 'days' : 'day')
      : this.t(plural ? 'js_unit_weeks' : 'js_unit_week', plural ? 'weeks' : 'week');
  }
  unitAbbr() { return this.estUnit === 'days' ? this.t('js_abbr_day', 'd') : this.t('js_abbr_week', 'wk'); }
  supplyDesc(roleK) {
    // pad-specific captions (js_padcap_*) so the shared js_supply_* used by the wipes box stays untouched
    if (roleK === 'quick' || roleK === 'starter') return this.t('js_padcap_starter', 'Dip a paw in');
    if (roleK === 'value') return this.t('js_padcap_value', 'Most pad for your money');
    return this.t('js_padcap_stock', 'Stock up, worry less');
  }
  refPerUnit() {
    let max = 0;
    this.pills.forEach((s) => { const v = this.variantObj(s); const c = this.parseCount(s); if (v && c) max = Math.max(max, v.price / c); });
    return max;
  }
  currentVariant() { return this.variantObj(this.pill); }

  // Grouped pills (• Tubs / • Travel packs). Headers show only when both groups
  // are present; otherwise a single ungrouped stack.
  #renderPills() {
    const wrap = this.querySelector('[data-pogi-sizes]');
    if (!wrap) return;
    const tubs = this.pills.filter((s) => this.formatOf(s) === 'tub');
    const packs = this.pills.filter((s) => this.formatOf(s) === 'packs');
    const showHeaders = tubs.length > 0 && packs.length > 0;
    const grp = (label, mod, items) => items.length
      ? `<div class="pogi-bb__group">${showHeaders ? `<div class="pogi-bb__group-header pogi-bb__group-header--${mod}"><span class="pogi-bb__group-dot"></span>${this.#esc(label)}</div>` : ''}<div class="pogi-bb__pills">${items.map((s) => this.#pill(s)).join('')}</div></div>`
      : '';
    wrap.innerHTML = grp(this.t('js_group_tub', 'Tubs'), 'tubs', tubs) + grp(this.t('js_group_packs', 'Travel packs'), 'packs', packs);
  }
  #pill(v) {
    const role = ROLE[this.parseCount(v)] || { t: '' };
    return `<button type="button" class="pogi-bb__pill" role="radio" data-pogi-size="${this.#esc(v)}" aria-checked="false">
      <span class="pogi-bb__pill-top"><span class="pogi-bb__pill-role${role.accent ? ' pogi-bb__pill-role--accent' : ''}">${role.k ? this.t('js_role_' + role.k, role.t) : ''}</span><span class="pogi-bb__pill-check" aria-hidden="true">✓</span></span>
      <span class="pogi-bb__pill-mid"><span class="pogi-bb__pill-size">${this.#esc(v)}</span><span class="pogi-bb__pill-price" data-pill-price></span></span>
      <span class="pogi-bb__pill-bot"><span class="pogi-bb__pill-per" data-pill-per></span><span class="pogi-bb__pill-save" data-pill-save></span></span>
      <span class="pogi-bb__pill-supply" data-pill-supply></span>
    </button>`;
  }

  #renderTiles() {
    const wrap = this.querySelector('[data-pogi-tiles]');
    if (!wrap || !this.tiles.length) return;
    if (this.tileKind === 'size') {
      let maxDim = 1;
      this.tiles.forEach((t) => { const d = this.dims(t); if (d) maxDim = Math.max(maxDim, d.w, d.h); });
      const scale = 40 / maxDim;
      // POGI FIX (2026-07-23): tiles DISPLAY smallest→largest (Medium, Large, XL).
      // this.tiles keeps variant/data order (Large first = default hero variant —
      // do NOT reorder the product option values), so the sort is render-only.
      // Keyed on the parsed dims (same parse as the glyph), so it holds in every
      // locale; if any value fails to parse, fall back to data order unchanged.
      const areas = this.tiles.map((t) => { const d = this.dims(t); return d ? d.w * d.h : null; });
      let ordered = this.tiles;
      if (areas.every((a) => a !== null)) {
        ordered = this.tiles.map((t, i) => [areas[i], t]).sort((a, b) => a[0] - b[0]).map((p) => p[1]);
      }
      wrap.innerHTML = ordered.map((t) => {
        const d = this.dims(t) || { w: 24, h: 24 };
        const name = String(t).split(/[（(]/)[0].trim(); // POGI FIX 2026-07-23: display-only split supports ASCII/full-width parentheses
        const sk = SIZE_KEY[`${d.w}x${d.h}`] || '';
        // EN uses the parsed inch dims (d.w/d.h); JA picks the nice cm (cm.wc/cm.hc).
        // When the size key is unknown, cm falls back to the parsed numbers so the JA
        // string still renders something sane.
        const cm = SIZE_CM[sk] || { wc: d.w, hc: d.h };
        const dimStr = this.t('js_dims', '[[w]] × [[h]] in', { w: d.w, h: d.h, wc: cm.wc, hc: cm.hc });
        const fit = sk ? this.t('js_fit_' + sk, SIZE_FIT_EN[sk]) : '';
        return `<button type="button" class="pogi-bb__sizetile" role="radio" data-pogi-tile="${this.#esc(t)}" aria-checked="false">
          <span class="pogi-bb__sizetile-glyphwrap"><span class="pogi-bb__sizetile-glyph" style="width:${(d.w * scale).toFixed(0)}px;height:${(d.h * scale).toFixed(0)}px"></span></span>
          <span class="pogi-bb__sizetile-name">${this.#esc(name)}</span>
          <span class="pogi-bb__sizetile-dims">${dimStr}</span>
          ${fit ? `<span class="pogi-bb__sizetile-fit">${fit}</span>` : ''}
        </button>`;
      }).join('');
    } else {
      wrap.innerHTML = this.tiles.map((s) => {
        const ff = /^\s*(?:fragrance-free|unscented|無香料?|sans parfum|ohne (?:parf(?:ü|ü)m|duftstoffe)|sin perfume|senza profumo|zonder (?:parfum|geur)|bezzapach[a-ząćęłńóśźż]*|sem perfume)\s*$/i.test(s); // exact-value ff detection, all 9 locales (NFC+NFD de); full phrases only — generic scent words (parfum/duft/geur/perfume) must NOT match
        const icon = ff ? ICONS.droplet : ICONS.sprout;
        const accent = ff ? 'var(--pogi-olive-deep)' : 'var(--pogi-olive)';
        const l1 = ff ? this.t('js_scent_ff_1', 'No fragrance added') : this.t('js_scent_fresh_1', 'Fresh, clean scent');
        const l2 = ff ? this.t('js_scent_ff_2', 'Ideal for sensitive dogs') : this.t('js_scent_fresh_2', 'Light everyday freshness');
        return `<button type="button" class="pogi-bb__scent" role="radio" data-pogi-tile="${this.#esc(s)}" aria-checked="false">
          <span class="pogi-bb__scent-icon" style="background:${accent}">${icon}</span>
          <span class="pogi-bb__scent-text"><span class="pogi-bb__scent-name">${this.#esc(s)}</span><span class="pogi-bb__scent-l1">${l1}</span><span class="pogi-bb__scent-l2">${l2}</span></span>
        </button>`;
      }).join('');
    }
  }

  // Convert a selling-plan interval to weeks (per BuyBox — intervals shown in weeks).
  #freqWeeks(name) {
    const pi = pogiParseFreq(name);
    if (!pi) return null;
    if (pi.u === 'month') return Math.round((pi.n * 30) / 7);
    if (pi.u === 'week') return pi.n;
    return Math.round(pi.n / 7);
  }
  #parseInterval(name) {
    return pogiParseFreq(name); // shared locale-proof parser (see POGI_FREQ_WORDS above)
  }
  // Per-variant interval config injected as variant.sub ("unit|opts|default").
  #cfgFor(variant) {
    const raw = variant && variant.sub;
    if (!raw) return null;
    const parts = String(raw).split('|');
    if (parts.length < 2 || !parts[1]) return null;
    return { u: parts[0], o: parts[1].split(',').map(Number), d: Number(parts[2]) };
  }
  // Recharge plans are product-level; show only the selected variant's configured
  // intervals (matched to live plans by parsed interval), default to its mid option.
  #applyVariantPlans() {
    const all = (this.data.sellingPlan && this.data.sellingPlan.plans) || [];
    const cfg = this.#cfgFor(this.currentVariant());
    let plans;
    if (cfg) {
      plans = cfg.o.map((c) => all.find((p) => { const pi = this.#parseInterval(p.name); return pi && pi.u === cfg.u && pi.n === c; })).filter(Boolean);
    } else {
      plans = all.slice(0, this.intervalCount);
    }
    this.intervalPlans = plans;
    const sig = plans.map((p) => p.id).join(',');
    if (sig !== this._planSig) {
      this._planSig = sig;
      const def = cfg ? plans.find((p) => { const pi = this.#parseInterval(p.name); return pi && pi.n === cfg.d; }) : null;
      this.planId = (def && def.id) || (plans[0] && plans[0].id) || null;
      this.#renderFrequencies();
    } else if (!plans.some((p) => p.id === this.planId)) {
      this.planId = (plans[0] && plans[0].id) || null;
    }
  }
  #renderFrequencies() {
    const wrap = this.querySelector('[data-pogi-freq-opts]');
    if (!wrap || !this.intervalPlans || !this.intervalPlans.length) return;
    const intervalUnit = (u, n) => {
      const plural = n > 1;
      if (u === 'month') return this.t(plural ? 'js_unit_months' : 'js_unit_month', plural ? 'months' : 'month');
      if (u === 'day') return this.t(plural ? 'js_unit_days' : 'js_unit_day', plural ? 'days' : 'day');
      return this.t(plural ? 'js_unit_weeks' : 'js_unit_week', plural ? 'weeks' : 'week');
    };
    wrap.innerHTML = this.intervalPlans.map((p) => {
      const pi = this.#parseInterval(p.name);
      const label = pi ? this.t('js_freq', '[[dur]]', { dur: this.t('js_dur', '[[n]] [[unit]]', { n: pi.n, unit: intervalUnit(pi.u, pi.n) }) }) : String(p.name);
      return `<button type="button" class="pogi-bb__freq-opt" role="radio" data-pogi-freq="${p.id}" aria-checked="${p.id === this.planId}">${label}</button>`;
    }).join('');
  }

  #bind() {
    this.addEventListener('click', (e) => {
      const pill = e.target.closest('[data-pogi-size]');
      const tile = e.target.closest('[data-pogi-tile]');
      const mode = e.target.closest('[data-pogi-mode]');
      const freq = e.target.closest('[data-pogi-freq]');
      const dt = e.target.closest('[data-pogi-desc-toggle]');
      const dec = e.target.closest('[data-pogi-qty-dec]');
      const inc = e.target.closest('[data-pogi-qty-inc]');
      if (pill) { this.pill = pill.dataset.pogiSize; this.#driveNative(); this.update(); }
      else if (tile) { this.tile = tile.dataset.pogiTile; this.#driveNative(); this.update(); }
      else if (mode) { this.subscribe = mode.dataset.pogiMode === 'subscribe'; this.update(); }
      else if (freq) { this.planId = parseInt(freq.dataset.pogiFreq, 10); this.update(); }
      else if (dt) { const d = this.querySelector('[data-pogi-desc]'); const o = d.classList.toggle('is-open'); dt.textContent = o ? this.t('read_less', 'Read less') : this.t('read_more', 'Read more'); dt.setAttribute('aria-expanded', String(o)); }
      else if (dec) { this.qty = Math.max(1, this.qty - 1); this.#syncQty(); }
      else if (inc) { this.qty += 1; this.#syncQty(); }
    });
    // A11Y (audit P3, 2026-07-23): ONE delegated keydown per component root —
    // roving-tabindex arrow-key support for the radio groups (see #roveKey).
    this.addEventListener('keydown', (e) => this.#roveKey(e));
  }

  /* ---- a11y: roving tabindex (audit P3 + countersign #19, 2026-07-23) ---- */
  // ONE tab stop per radio group: tabindex 0 on the checked ELIGIBLE radio
  // (first eligible if none), -1 on every other radio — hidden/disabled ones
  // included, so they can never hold a stale tab stop. Called at the END of
  // update(), AFTER the pill display-sync and [hidden] toggles, so eligibility
  // is judged on the pass's final visibility state. Behavior-additive: click
  // handling is untouched.
  #rove() {
    this.querySelectorAll(POGI_ROVE_GROUPS).forEach((g) => {
      const all = [...g.querySelectorAll('[role="radio"]')];
      const radios = all.filter(POGI_ROVE_ELIGIBLE);
      all.forEach((b) => b.setAttribute('tabindex', '-1')); // hidden/disabled are never the tab stop
      if (!radios.length) return;
      const on = radios.find((b) => b.getAttribute('aria-checked') === 'true') || radios[0];
      on.setAttribute('tabindex', '0'); // checked-and-eligible, else first ELIGIBLE
    });
  }
  // Arrow keys move focus AND activate the next/prev radio with wrap-around;
  // Home/End jump to first/last. Activation goes through .click() — the exact
  // path pointer input takes — so selection, native-picker drive and update()
  // all reuse the existing delegated click handler above.
  #roveKey(e) {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) return;
    const radio = e.target.closest('[role="radio"]');
    const group = radio && radio.closest(POGI_ROVE_GROUPS);
    if (!group) return; // not one of our groups (e.g. the sizing modal) — leave the key alone
    const radios = [...group.querySelectorAll('[role="radio"]')].filter(POGI_ROVE_ELIGIBLE); // arrows skip hidden/disabled radios
    const i = radios.indexOf(radio);
    if (i < 0) return;
    e.preventDefault();
    const next = e.key === 'Home' ? radios[0]
      : e.key === 'End' ? radios[radios.length - 1]
        : (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? radios[(i + 1) % radios.length]
          : radios[(i - 1 + radios.length) % radios.length];
    if (next === radio) return;
    next.focus();
    next.click();
    // A click can rebuild the clicked group's innerHTML (e.g. compostable freqs
    // re-render on selection) — re-focus the fresh tab stop so keyboard context
    // is never dropped mid-interaction.
    if (!next.isConnected) { const nb = group.querySelector('[role="radio"][tabindex="0"]'); if (nb) nb.focus(); }
  }
  #setNativeOption(fieldsetIndex, value) {
    if (!this.nativePicker || fieldsetIndex < 0) return;
    const inputs = this.nativePicker.querySelectorAll(`input[data-fieldset-index="${fieldsetIndex}"]`);
    for (const input of inputs) {
      if (input.value === value && !input.checked) { input.checked = true; input.dispatchEvent(new Event('input', { bubbles: true })); input.dispatchEvent(new Event('change', { bubbles: true })); return; }
    }
  }
  #driveNative() {
    if (this.tile != null) this.#setNativeOption(this.tilePos - 1, this.tile);
    if (this.pill != null) this.#setNativeOption(this.pillsPos - 1, this.pill);
  }

  plan() { const sp = this.data.sellingPlan; if (!sp || !sp.plans) return null; return sp.plans.find((p) => p.id === this.planId) || sp.plans[0]; }
  discountFrac(variant) {
    const p = this.plan(); if (!p) return 0;
    if (p.adjType === 'percentage') return p.adjValue / 100;
    if (p.adjType === 'fixed_amount') return Math.min(1, p.adjValue / variant.price);
    if (p.adjType === 'price') return Math.max(0, 1 - p.adjValue / variant.price);
    return 0;
  }

  update() {
    this.#applyVariantPlans();
    const variant = this.currentVariant();
    const ref = this.refPerUnit();

    this.querySelectorAll('[data-pogi-size]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.pogiSize === this.pill)));
    this.querySelectorAll('[data-pogi-tile]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.pogiTile === this.tile)));
    this.querySelectorAll('[data-pogi-mode]').forEach((b) => b.setAttribute('aria-checked', String((b.dataset.pogiMode === 'subscribe') === this.subscribe)));
    this.querySelectorAll('[data-pogi-freq]').forEach((b) => b.setAttribute('aria-checked', String(parseInt(b.dataset.pogiFreq, 10) === this.planId)));
    const tlab = this.querySelector('[data-pogi-tile-label]');
    if (tlab && this.tile) tlab.textContent = this.tileKind === 'size' ? String(this.tile).split(/[（(]/)[0].trim() : this.tile;

    this.querySelectorAll('[data-pogi-size]').forEach((btn) => {
      const pv = btn.dataset.pogiSize;
      const v = this.variantObj(pv);
      const count = this.parseCount(pv);
      const role = ROLE[count] || { t: '' };
      const setT = (sel, val) => { const el = btn.querySelector(sel); if (el) el.textContent = val; };
      if (v) {
        const n = this.estN(count);
        setT('[data-pill-price]', this.priceMoney(v));
        const perLine = (!count || !SHOW_DERIVED) ? '' : this.zeroDec
          ? this.t('js_per_unit_money', '[[money]]/[[unit]] · ~[[n]] [[abbr]]', { money: this.money(Math.round(v.price / count)), unit: this.unitNoun, n, abbr: this.unitAbbr() })
          : this.t('js_per_unit', '[[cents]]¢/[[unit]] · ~[[n]] [[abbr]]', { cents: Math.round(v.price / count), unit: this.unitNoun, n, abbr: this.unitAbbr() });
        setT('[data-pill-per]', perLine);
        const saveCents = ref > 0 && count > 0 ? Math.round(ref * count - v.price) : 0;
        setT('[data-pill-save]', (SHOW_DERIVED && saveCents > 0) ? this.t('js_save_caps', 'SAVE [[amount]]', { amount: this.moneyWhole(saveCents) }) : '');
        setT('[data-pill-supply]', count > 0 ? this.t('js_supply_line', '[[desc]] · [[n]]-[[unit]] supply', { desc: this.supplyDesc(role.k), n, unit: this.unitWord(1) }) : '');
        btn.style.display = '';
      } else { btn.style.display = 'none'; }
    });

    const est = this.querySelector('[data-pogi-estimate]');
    const count = this.parseCount(this.pill);
    if (est && count > 0) {
      const n = this.estN(count);
      est.querySelector('[data-pogi-est-pack]').textContent = this.pill;
      est.querySelector('[data-pogi-est-weeks]').textContent = this.t('js_dur', '[[n]] [[unit]]', { n, unit: this.unitWord(n) });
      est.hidden = false;
    } else if (est) { est.hidden = true; }

    if (variant) {
      const frac = this.discountFrac(variant);
      const sub = Math.round(variant.price * (1 - frac));
      const setT = (sel, val) => { const el = this.querySelector(sel); if (el) el.textContent = val; };
      // On USD: exact JS money() (byte-identical). Off the base currency: the subscribe
      // figure is a JS-derived discount we can't presentment-convert, so fall back to the
      // variant's true presentment base price (better than a wrong USD-cents number).
      setT('[data-pogi-sub-price]', SHOW_DERIVED ? this.money(sub) : (this.subPriceMoney(variant) || this.priceMoney(variant)));
      setT('[data-pogi-sub-was]', frac > 0 ? (SHOW_DERIVED ? this.money(variant.price) : this.priceMoney(variant)) : '');
      setT('[data-pogi-ot-price]', this.priceMoney(variant));
      const badge = this.querySelector('[data-pogi-save-badge]');
      if (badge) badge.textContent = frac > 0 ? (SHOW_DERIVED ? this.t('js_save', 'Save [[amount]]', { amount: this.money(variant.price - sub) }) : this.t('js_save_pct', '[[pct]]% off', { pct: Math.round(frac * 100) })) : '';
    }

    const subx = this.querySelector('[data-pogi-subx]');
    if (subx) subx.hidden = !this.subscribe;
    const subxDesc = this.querySelector('[data-pogi-subx-desc]');
    if (subxDesc && count > 0) {
      const n = this.estN(count);
      const dur = this.t('js_dur', '[[n]] [[unit]]', { n, unit: this.unitWord(n) });
      subxDesc.textContent = this.t('js_subx_desc', '[[pack]] lasts about [[dur]]. We send your next pack just before you run low.', { pack: this.pill, dur });
    }

    this.#ensureSellingPlanInput();
    const planInput = this.nativeForm && this.nativeForm.querySelector('input[name="selling_plan"]');
    if (planInput) planInput.value = this.subscribe && this.planId ? this.planId : '';
    // Write the variant id synchronously too (mirrors Horizon's async picker write),
    // closing the race where a fast option-change → ATC could submit a stale id.
    const idInput = this.nativeForm && this.nativeForm.querySelector('input[name="id"]');
    if (idInput && variant) { idInput.value = variant.id; idInput.dataset.pogiSync = '1'; } // POGI: marks this form synchronously managed (product-form #getIntendedVariantId)
    const atc = this.querySelector('[data-pogi-atc]');
    const atcLabel = this.querySelector('[data-pogi-atc-label]');
    if (variant && atc) {
      atc.disabled = !variant.available;
      const frac = this.discountFrac(variant);
      const price = this.subscribe ? Math.round(variant.price * (1 - frac)) : variant.price;
      // USD → exact JS money(); off base currency → variant presentment base price.
      const priceStr = SHOW_DERIVED ? this.money(price) : (this.subscribe ? (this.subPriceMoney(variant) || this.priceMoney(variant)) : this.priceMoney(variant));
      atcLabel.textContent = variant.available ? (this.subscribe ? this.t('js_atc_sub', 'Start my routine · ') : this.t('js_atc_add', 'Add to cart · ')) + priceStr : this.t('js_sold_out', 'Sold out');
    }

    // free-shipping nudge — subscriptions always ship free; one-time counts toward the threshold
    const ship = this.querySelector('[data-pogi-ship]');
    if (ship && variant) {
      const msg = ship.querySelector('[data-pogi-ship-msg]');
      const fill = ship.querySelector('[data-pogi-ship-fill]');
      if (this.subscribe) {
        ship.classList.add('is-unlocked');
        if (msg) msg.innerHTML = '<span style="white-space:nowrap">' + this.t('js_ship_always_html', '<strong>Free shipping, always.</strong>') + '</span> <span style="white-space:nowrap">' + pogiArrives(this.I) + '</span>';
        if (fill) fill.style.width = '100%';
      } else {
        const total = variant.price * this.qty;
        const remaining = Math.max(0, this.shipThreshold - total);
        if (remaining === 0) {
          ship.classList.add('is-unlocked');
          if (msg) msg.innerHTML = '<span style="white-space:nowrap">' + this.t('js_ship_unlocked_html', '<strong>Free shipping unlocked.</strong>') + '</span> <span style="white-space:nowrap">' + pogiArrives(this.I) + '</span>';
        } else {
          ship.classList.add('is-unlocked');
          if (msg) msg.innerHTML = this.t('js_ship_remaining_html', "You're <strong>[[amount]]</strong> from free shipping.", { amount: this.money(remaining) });
        }
        if (fill) fill.style.width = '100%';
      }
    }
    // Roving tabindex LAST — after the pill display-sync and the [hidden] panel
    // toggles above, so a hidden/disabled radio is never left holding a group's
    // tab stop (Codex countersign #19, 2026-07-23).
    this.#rove();
  }

  #ensureSellingPlanInput() {
    if (!this.nativeForm) return;
    if (!this.nativeForm.querySelector('input[name="selling_plan"]')) {
      const i = document.createElement('input'); i.type = 'hidden'; i.name = 'selling_plan'; this.nativeForm.appendChild(i);
    }
  }
  #syncQty() {
    const out = this.querySelector('[data-pogi-qty]');
    if (out) out.textContent = this.qty;
    const nq = this.nativeForm && this.nativeForm.querySelector('input[name="quantity"]');
    if (nq) { nq.value = this.qty; nq.dispatchEvent(new Event('change', { bubbles: true })); }
    this.update();
  }
}

if (!customElements.get('pogi-buy-box-pads')) {
  customElements.define('pogi-buy-box-pads', PogiBuyBoxPads);
}
